import { useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import qs from 'qs';

import { Add } from 'iconsax-react';

import useTiclickTableConfig from '@shared/hooks/use-ticlick-table-config';
import { TABLES_KEYS } from '@shared/constants/tables-keys';
import { entities } from '@shared/constants/entities';
import { HasPermission } from '@features/roles/guards/has-permission';
import { Button } from '@src/design-system/button';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@src/design-system/dialog';
import type { ActionItemProps } from '@src/design-system/molecules/ticlick-table/ticlick-table-table-actions';
import { CreateOrEditReminderTemplate } from '@src/shared/features/reminders/dialogs/create-or-edit-reminder-template';
import type { TReminderTemplate } from '@src/shared/types/reminder-template';

import reminderTemplateApi from '../../services/reminder-template-api';
import { TemplatesLayoutSwitcher } from '../templates-layout-switcher';
import { ReminderTemplateTableRowActions } from '../reminder-template-table-row-actions';
import { reminderTemplatesColumns } from './reminder-templates-columns';

function useReminderTemplateUrlFilters() {
  const { search } = useLocation();
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    const queryParams = qs.parse(search, { ignoreQueryPrefix: true });
    const tableFilters = queryParams[TABLES_KEYS.reminderTemplatesTable] as
      | { name?: string }
      | undefined;

    const nameFromTable =
      typeof tableFilters?.name === 'string' ? tableFilters.name : undefined;

    return {
      name: searchParams.get('name') || nameFromTable || undefined,
      channel: searchParams.get('channel') || undefined,
      created_by: Number(searchParams.get('created_by')) || undefined,
      content_type: searchParams.get('content_type') || undefined,
    };
  }, [search, searchParams]);
}

export function useReminderTemplatesTable() {
  const urlFilters = useReminderTemplateUrlFilters();

  const table = useTiclickTableConfig({
    options: { columns: reminderTemplatesColumns },
    identifyingData: { tableKey: TABLES_KEYS.reminderTemplatesTable },
    fetchingData: {
      fetchFn: reminderTemplateApi.useFetch,
      query: urlFilters,
    },
    rowActions: [
      {
        key: 'actions',
        minWidth: 120,
        render: (row) => (
          <ReminderTemplateTableRowActions reminderTemplate={row.original} />
        ),
      },
    ],
  });

  const toolbarActions: ActionItemProps<TReminderTemplate>[] = [
    {
      key: 'add',
      visibility: 'none-selection',
      minWidth: 130,
      render: () => (
        <HasPermission
          action='add'
          entity={entities.reminderTemplate}
        >
          <Dialog>
            <DialogTrigger asChild>
              <Button
                size='sm'
                className='flex justify-center items-center rounded-xl'
              >
                <Add size={20} />
                Create Template
              </Button>
            </DialogTrigger>
            <DialogContent
              onOpenAutoFocus={(e) => e.preventDefault()}
              className='flex w-full max-w-[78%] flex-col overflow-hidden'
            >
              <CreateOrEditReminderTemplate />
            </DialogContent>
          </Dialog>
        </HasPermission>
      ),
    },
    {
      key: 'layout-switcher',
      render: () => <TemplatesLayoutSwitcher />,
      minWidth: 80,
      visibility: 'common',
    },
  ];

  return { table, toolbarActions };
}
